/**
 * ImageFilterManager.ts;
 * Gère les filtres d'image appliqués aux objets Konva;
 */
import Konva from 'konva';
import { logger } from './utils/ProfessionalLogger';
export interface ImageFilter {
  brightness: number;
  contrast: number;
  blur: number;
  saturation: number;
}
export class ImageFilterManager {
  private static readonly DEFAULT_FILTERS: ImageFilter = {
    brightness: 0,
    contrast: 0,
    blur: 0,
    saturation: 0,
  };
  /**
   * Applique des filtres à un nœud Konva;
   */
  public static applyFilters(node: Konva.Node, filters: Partial<ImageFilter>): void {
    if (!(node instanceof Konva.Image)) {
      logger.warn("Les filtres ne peuvent être appliqués qu'aux images");
      return;
    }
    const imageNode = node as Konva.Image;
    const currentFilters = imageNode.getAttr('imageFilters') || this.DEFAULT_FILTERS;
    const newFilters = { ...currentFilters, ...filters };
    // Déterminer quels filtres Konva utiliser;
    const konvaFilters: Array<
      | typeof Konva.Filters.Brighten
      | typeof Konva.Filters.Contrast
      | typeof Konva.Filters.Blur
      | typeof Konva.Filters.HSV
    > = [];
    if (newFilters.brightness !== 0) {
      konvaFilters.push(Konva.Filters.Brighten);
    }
    if (newFilters.contrast !== 0) {
      konvaFilters.push(Konva.Filters.Contrast);
    }
    if (newFilters.blur !== 0) {
      konvaFilters.push(Konva.Filters.Blur);
    }
    if (newFilters.saturation !== 0) {
      konvaFilters.push(Konva.Filters.HSV);
    }
    // Appliquer les filtres Konva;
    if (konvaFilters.length > 0) {
      imageNode.filters(konvaFilters);
      imageNode.brightness(newFilters.brightness / 100);
      imageNode.contrast(newFilters.contrast / 100);
      imageNode.blurRadius(newFilters.blur);
      // Gérer la saturation via HSV;
      if (newFilters.saturation !== 0) {
        const currentHue = imageNode.getAttr('hue') || 0;
        const currentValue = imageNode.getAttr('value') || 0;
        imageNode.hue(currentHue);
        imageNode.saturation(newFilters.saturation / 100);
        imageNode.value(currentValue);
      }
    } else {
      imageNode.filters([]);
      imageNode.brightness(0);
      imageNode.contrast(0);
      imageNode.blurRadius(0);
      imageNode.saturation(0);
    }
    // Sauvegarder les filtres dans les attributs;
    imageNode.setAttr('imageFilters', newFilters);
    imageNode.cache();
  }
  /**
   * Obtient les filtres actuels d'un nœud;
   */
  public static getFilters(node: Konva.Node): ImageFilter {
    if (!(node instanceof Konva.Image)) {
      return { ...this.DEFAULT_FILTERS };
    }
    return node.getAttr('imageFilters') || { ...this.DEFAULT_FILTERS };
  }
  /**
   * Réinitialise tous les filtres d'un nœud;
   */
  public static resetFilters(node: Konva.Node): void {
    if (!(node instanceof Konva.Image)) {
      return;
    }
    const imageNode = node as Konva.Image;
    imageNode.filters([]);
    imageNode.brightness(0);
    imageNode.contrast(0);
    imageNode.blurRadius(0);
    imageNode.setAttr('imageFilters', { ...this.DEFAULT_FILTERS });
    imageNode.cache();
  }
  /**
   * Vérifie si un nœud a des filtres appliqués;
   */
  public static hasFilters(node: Konva.Node): boolean {
    if (!(node instanceof Konva.Image)) {
      return false;
    }
    const filters = node.getAttr('imageFilters') || this.DEFAULT_FILTERS;
    return Object.values(filters).some(value => value !== 0);
  }
}
